import React, { useState } from 'react';
import { Box, Icon } from '@amzn/awsui-components-react';

// Annotate each node with a stable path id and normalized children so leaf detection
// (a node with no children) is deterministic regardless of the author's JSON.
const annotate = (nodes, prefix) => (Array.isArray(nodes) ? nodes : []).map((node, i) => {
    const id = `${prefix}/${i}`;
    const children = annotate(node?.children, id);
    return { ...node, __id: id, __children: children.length ? children : undefined };
});

/**
 * Renders a JSON-defined tree where only leaves (nodes with no children) are selectable.
 *
 * Controlled multi-select: pass `selectedValues` (array) + `onToggleLeaf(leafValue)`.
 * Uncontrolled (e.g. the author preview): omit `onToggleLeaf` and selection is kept
 * internally and ephemerally, just to demonstrate the click behavior.
 */
const FlexicoTreeView = ({ json, selectedValues = null, onToggleLeaf = null, disabled = false }) => {
    const [expanded, setExpanded] = useState([]);
    const [internalSelected, setInternalSelected] = useState([]);
    const controlled = typeof onToggleLeaf === 'function';
    const selected = controlled ? (Array.isArray(selectedValues) ? selectedValues : []) : internalSelected;

    let treeData = null;
    let parseError = null;
    try {
        treeData = json ? JSON.parse(json) : [];
    } catch (e) {
        parseError = 'Invalid tree JSON: ' + (e?.message || 'could not parse');
    }
    if (parseError) {
        return <Box><span className="custom-new-upload__error-message ">{parseError}</span></Box>;
    }

    const roots = annotate(treeData, '');
    const toggleNode = (id) => setExpanded(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    const toggleLeaf = (leafValue) => {
        if (disabled) return;
        if (controlled) { onToggleLeaf(leafValue); return; }
        setInternalSelected(prev => (prev.includes(leafValue) ? prev.filter(v => v !== leafValue) : [...prev, leafValue]));
    };

    const renderNodes = (nodes, depth) => nodes.map((node) => {
        const isLeaf = !node.__children;
        const isOpen = expanded.includes(node.__id);
        const leafValue = node.value ?? node.label;
        const isSelected = isLeaf && selected.includes(leafValue);
        return (
            <div key={node.__id}>
                <div
                    className={`flexico-tree__row${isSelected ? ' flexico-tree__row--selected' : ''}`}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '3px 6px', paddingLeft: `${depth * 20 + 6}px`, borderRadius: 6,
                        cursor: (isLeaf && disabled) ? 'default' : 'pointer',
                        fontWeight: isSelected ? 700 : 400,
                        background: isSelected ? '#f0f7ff' : undefined,
                        boxShadow: isSelected ? 'inset 2px 0 0 #0972d3' : undefined,
                    }}
                    onClick={() => { if (isLeaf) { toggleLeaf(leafValue); } else { toggleNode(node.__id); } }}
                >
                    {isLeaf
                        ? <Icon name={isSelected ? 'check' : 'file'} variant={isSelected ? 'success' : 'subtle'} size="small" />
                        : <Icon name={isOpen ? 'caret-down-filled' : 'caret-right-filled'} size="small" />}
                    <span>{node.label}</span>
                </div>
                {!isLeaf && isOpen && renderNodes(node.__children, depth + 1)}
            </div>
        );
    });

    if (roots.length === 0) {
        return <Box><span className="small_grey_text_italics">Nothing to preview yet — enter tree JSON.</span></Box>;
    }
    return <Box>{renderNodes(roots, 0)}</Box>;
};

export default FlexicoTreeView;
